// Data-access layer — the single place that talks to PocketBase collections.
//
// Hybrid model:
//   • verbs / grammar_articles are PUBLIC → fetched for everyone (guests too).
//   • user_workspace is OWNER-SCOPED → fetched only when authenticated, then
//     overlaid onto the public items so a view can show pins/notes inline.
//
// Every fetch degrades gracefully: if the backend is unreachable, public
// fetches fall back to the bundled local dataset so the reference app still
// works fully offline / pre-deploy.
import { pb } from "./pocketbase.js";
import { isAuthed, currentUser } from "./auth.js";
import { ALL_VERBS } from "../data/verbs.js";
import { LOCAL_ARTICLES } from "../data/articles.js";
import { VOCABULARY_CARDS } from "../data/vocabulary.js";
import { nextSchedule } from "./srs.js";

// ---- Public reads -------------------------------------------------------

export async function fetchVerbs() {
  try {
    return await pb.collection("verbs").getFullList({ sort: "rank" });
  } catch {
    // Offline / no backend → use the bundled dataset. Shape is normalised so
    // views can treat local and remote records identically.
    return ALL_VERBS.map((v) => ({
      id: `local:${v.inf}`,
      infinitive: v.inf,
      english_translation: v.en,
      pattern_group: v.group,
      regularity_status: v.group === "regular" ? "regular" : "irregular",
      rank: v.rank,
      is_top: !!v.top,
      emoji: v.emoji || "",
      note: v.note || "",
      _local: true,
    }));
  }
}

export async function fetchArticles() {
  try {
    const remote = await pb.collection("grammar_articles").getFullList({ sort: "difficulty_level" });
    // An empty remote collection (schema imported but not yet seeded) still
    // falls back to the bundled set so the Grammar Map is never blank.
    return remote.length ? remote : LOCAL_ARTICLES;
  } catch {
    return LOCAL_ARTICLES;
  }
}

export async function fetchVocabulary() {
  try {
    const remote = await pb.collection("vocabulary").getFullList({ sort: "rank" });
    return remote.length ? remote : localVocabulary();
  } catch {
    return localVocabulary();
  }
}

// Bundled vocabulary, shaped like PB records so views treat both identically.
function localVocabulary() {
  return VOCABULARY_CARDS.map((c) => ({
    id: `local:${c.spanish}`,
    ...c,
    _local: true,
  }));
}

// Dictionary is HOST-BUILT only (license-isolated, filled by
// scripts/dictionary_build.js) — there is no bundled fallback. Pre-seed or
// offline it simply returns [] and the view shows a "not loaded yet" note.
export async function fetchDictionary() {
  try {
    return await pb.collection("dictionary_entries").getFullList({ sort: "headword" });
  } catch {
    return [];
  }
}

// ---- Owner-scoped reads -------------------------------------------------

export async function fetchWorkspace() {
  if (!isAuthed()) return [];
  const uid = currentUser()?.id;
  if (!uid) return [];
  try {
    return await pb.collection("user_workspace").getFullList({
      filter: `user_id = "${uid}"`,
    });
  } catch {
    return [];
  }
}

// Merge workspace rows (pins + notes) onto public items, keyed by item_id.
// Guests get is_pinned:false and notes:null on every item.
export function overlay(items, workspace, collectionType) {
  const byItem = new Map();
  for (const w of workspace) {
    if (collectionType && w.collection_type !== collectionType) continue;
    byItem.set(w.item_id, w);
  }
  return items.map((item) => {
    const w = byItem.get(item.id);
    return {
      ...item,
      is_pinned: w ? !!w.is_pinned : false,
      user_notes_text: w ? w.user_notes_text || "" : null,
      _workspace_id: w ? w.id : null,
    };
  });
}

// ---- Owner-scoped writes (no-op-safe for guests) ------------------------

async function findWorkspaceRow(collectionType, itemId) {
  const uid = currentUser()?.id;
  if (!uid) return null;
  try {
    return await pb
      .collection("user_workspace")
      .getFirstListItem(
        `user_id = "${uid}" && collection_type = "${collectionType}" && item_id = "${itemId}"`
      );
  } catch {
    return null;
  }
}

export async function setPinned(collectionType, itemId, isPinned) {
  if (!isAuthed()) throw new Error("Sign in to pin items.");
  const uid = currentUser().id;
  const existing = await findWorkspaceRow(collectionType, itemId);
  if (existing) {
    return pb.collection("user_workspace").update(existing.id, { is_pinned: isPinned });
  }
  return pb.collection("user_workspace").create({
    user_id: uid,
    collection_type: collectionType,
    item_id: itemId,
    is_pinned: isPinned,
    user_notes_text: "",
    last_reviewed_at: new Date().toISOString(),
  });
}

export async function saveNotes(collectionType, itemId, notes) {
  if (!isAuthed()) throw new Error("Sign in to save notes.");
  const uid = currentUser().id;
  const existing = await findWorkspaceRow(collectionType, itemId);
  const payload = {
    user_notes_text: notes,
    last_reviewed_at: new Date().toISOString(),
  };
  if (existing) {
    return pb.collection("user_workspace").update(existing.id, payload);
  }
  return pb.collection("user_workspace").create({
    user_id: uid,
    collection_type: collectionType,
    item_id: itemId,
    is_pinned: false,
    ...payload,
  });
}

// ---- Spaced-review writes (Leitner-lite) --------------------------------

// Record one review of an item with a rating ("known" | "learning") and roll
// its Leitner schedule forward. Upserts the owner's workspace row.
export async function recordReview(collectionType, itemId, rating) {
  if (!isAuthed()) throw new Error("Sign in to track reviews.");
  const uid = currentUser().id;
  const existing = await findWorkspaceRow(collectionType, itemId);
  const sched = nextSchedule(existing, rating);
  if (existing) {
    return pb.collection("user_workspace").update(existing.id, sched);
  }
  return pb.collection("user_workspace").create({
    user_id: uid,
    collection_type: collectionType,
    item_id: itemId,
    is_pinned: false,
    user_notes_text: "",
    ...sched,
  });
}
